import { z } from "zod";
import { SegmentDefinitionSchema } from "./segment.js";
// Configurable analytics aggregation over adRequest (requests) / adResponse
// (responses). The request is a flat GROUP BY: rows = (time bucket × selected
// value-combination), with two metrics (raw count + distinct devices). See
// .omc/plans/analytics-configurable-aggregation.md.
//
// `fieldKeyEnum` is the canonical wire vocabulary shared by the frontend picker
// and the backend field registry (apps/backend/src/modules/analytics/fields.ts
// is the single source of truth for how each key maps to a column). Keeping the
// enum here means the request is Zod-validated before it reaches the route, and
// unknown keys are rejected (400) both here and again in the registry.
export const AnalyticsFieldKeySchema = z.enum([
    // request-side dims (adRequest)
    "country",
    "region",
    "city",
    "platform",
    "deviceType",
    "channel",
    "kind",
    "isPaid",
    "livestream",
    "gender",
    "provider",
    "source",
    "categories",
    // request-side dims from the exchange capture (adRequest, migration 0033).
    // Only dimensions with a bounded, groupable value: a page URL or an auction id
    // is unique per request and would make one row per request, and the network
    // fields are evidence rather than dimensions.
    "siteDomain",
    "appBundle",
    "trafficType",
    "adFormat",
    "publisherId",
    "os",
    "language",
    "dealId",
    // response-side dims (adResponse) — Campaign category
    "campaign",
    "segment",
    "creative",
]);
export const AnalyticsSourceSchema = z.enum(["requests", "responses"]);
export const AnalyticsBucketSchema = z.enum(["hour", "day", "week", "month"]);
// Source-dependent metrics (responses only). Each selected status emits two
// dynamic columns `<key>_total` and `<key>_devices`.
//   • bid          — the EXISTENCE of an adResponse row (no event). Special:
//                    total = COUNT(DISTINCT r.uuid), devices = COUNT(DISTINCT
//                    r.deviceId). STATUS_EVENT_CODE.bid = null.
//   • won/impression/loss/billable/nobid — an adResponseEvent with that status
//                    code; total/devices are FILTER (WHERE ev.status = code).
// AdResponseStatus (adEvent.ts): NoBid 0, Loss 1, Win 2, Billable 3, Impression
// 4. Only Impression is populated today; the rest must still be selectable.
export const AnalyticsStatusKeySchema = z.enum([
    "bid",
    "won",
    "impression",
    "loss",
    "billable",
    "nobid",
]);
// status key → AdResponseEvent.status code. `bid` is special (null = the
// adResponse row itself, no event). Codes are the ONLY ints ever interpolated
// into the metric SQL (never raw user strings).
export const STATUS_EVENT_CODE = {
    bid: null,
    won: 2,
    impression: 4,
    loss: 1,
    billable: 3,
    nobid: 0,
};
// Kyiv wall-clock range from the dashboard DateRangePicker. The route converts
// these to UTC instants before binding the raw `createdAt` window (to exclusive).
// Money metrics (responses only — adResponse carries the prices). Each emits one
// dynamic column of the same name holding a DECIMAL STRING, so an exact
// micro-dollar figure never passes through a float on its way to the browser.
//
//   • spend    what we pay the publisher   (buy side)
//   • revenue  what the buyer pays us      (sell side)
//   • profit   revenue − spend
//   • ecpm     revenue per thousand impressions, from this row's own sums
//   • margin   profit ÷ revenue × 100, from this row's own sums
//
// The ratios are computed per row from that row's absolutes, which is correct
// for a row. A TOTAL across rows must recompute them from the summed absolutes
// rather than averaging the row values (see `formulas` in exchange.ts).
//
// Billable basis follows money.sql: a standard campaign's serve always counts, a
// transit campaign's only once it produced a confirmed impression. So these
// figures match the money board and the public statistics link by construction.
export const AnalyticsMoneyKeySchema = z.enum([
    "spend",
    "revenue",
    "profit",
    "ecpm",
    "margin",
]);
// Exchange counter metrics. These come from `exchangeCounter`, a per-minute
// aggregate, NOT from the row tables — which is the point: a no-bid, a timeout,
// a throttle and a skipped DSP call produce no row anywhere else, and counting
// requests from the counters avoids touching a 1.79 TB table at all.
//
// The counters carry only provider and campaign as dimensions, so a
// report can be served from them only when every selected groupBy key is one of
// those. Otherwise the request falls through to the row tables, where these
// metrics are unavailable (hence the 400 described on the request schema).
export const AnalyticsCounterKeySchema = z.enum([
    "sspRequests",
    "sspThrottled",
    "sspPaused",
    "sspFiltered",
    "sspWins",
    "dspRequests",
    "dspSkippedQps",
    "dspResponses",
    "dspNoBid",
    "dspTimeouts",
    "dspHttpErrors",
    "invalidBids",
    "bidsBelowFloor",
    "dspWins",
    "validBids",
]);
// counter metric key → ExchangeMetric id. Kept beside the enum so the two cannot
// drift; the backend maps through this rather than re-deriving ids.
export const COUNTER_METRIC_ID = {
    sspRequests: 1,
    sspThrottled: 2,
    sspPaused: 3,
    sspFiltered: 4,
    sspWins: 5,
    dspRequests: 10,
    dspSkippedQps: 11,
    dspResponses: 13,
    dspNoBid: 14,
    dspTimeouts: 15,
    dspHttpErrors: 16,
    invalidBids: 17,
    bidsBelowFloor: 18,
    dspWins: 19,
    validBids: 21,
};
// The dimensions `exchangeCounter` actually carries. A counter-metric request
// whose groupBy stays inside this set is answered from the per-minute aggregate.
// `segment` is NOT here on purpose, even though exchangeCounter has the column.
// Nothing on the serving path passes a segment id when it increments a counter,
// so every stored row carries the zero uuid there — offering it as a breakdown
// would produce one row labelled "unknown" and nothing else. Add it back in the
// same change that starts populating it.
export const COUNTER_DIMENSIONS = [
    "provider",
    "campaign",
];
export const AnalyticsRangeSchema = z.object({
    from: z.string(),
    to: z.string(),
});
export const AnalyticsAggregateRequestSchema = z.object({
    source: AnalyticsSourceSchema,
    // ordered: order = GROUP BY order = ORDER BY order. MAY be empty → a pure
    // time series (one count/distinctDevices per bucket).
    groupBy: z.array(AnalyticsFieldKeySchema).min(0),
    bucket: AnalyticsBucketSchema,
    range: AnalyticsRangeSchema,
    // Reuses the segment builder's definition shape (in/nin only, applied here in
    // Include-only/AND mode). Response-side keys (campaign/segment/creative) are
    // routed to the analytics response-builder; the rest reuse segmentFiltersToSql.
    filter: SegmentDefinitionSchema,
    // responses-only: which status metric groups to emit. Empty/absent → ['bid'].
    // Ignored for the requests source (one fixed `requests` metric group).
    statuses: z.array(AnalyticsStatusKeySchema).optional(),
    // Money columns (responses only). Rejected with 400 on `requests`, which
    // carries no prices.
    money: z.array(AnalyticsMoneyKeySchema).optional(),
    // Exchange counter columns. Rejected with 400 when any groupBy key is outside
    // COUNTER_DIMENSIONS, because the per-minute aggregate cannot be broken down
    // by a dimension it does not store.
    counters: z.array(AnalyticsCounterKeySchema).optional(),
});
// A row's group + metric columns are dynamic (catchall). `bucket` is the only
// fixed column (a Kyiv wall-clock YYYY-MM-DDThh:mm:ss string). The rest:
//   • one column per groupBy key (id-valued — names resolved client-side)
//   • metric columns:
//       - source=requests  → `requests_total`, `requests_devices`
//       - source=responses → `<statusKey>_total`, `<statusKey>_devices` per
//         selected status (e.g. bid_total, bid_devices, impression_total, …)
//       - money     → one column per selected key, named for it, a DECIMAL
//                     STRING (spend, revenue, profit, ecpm, margin)
//       - counters  → one integer column per selected key (sspRequests, …)
export const AnalyticsAggregateRowSchema = z
    .object({
    bucket: z.string(),
})
    // z.array(z.string()) is load-bearing, not defensive: two dimensions group by
    // a text[] COLUMN (categories, dealId), so the group id for those rows IS an
    // array. Leaving it out of the union makes the response serializer mangle the
    // one column the row is keyed by.
    .catchall(z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.string()),
]));
export const AnalyticsAggregateResponseSchema = z.object({
    rows: z.array(AnalyticsAggregateRowSchema),
    // true when the LIMIT (5000) was hit → results are ordered-truncated.
    truncated: z.boolean(),
});
//# sourceMappingURL=analytics.js.map